export const DEFAULT_PAGE_TYPE = "excursions_visites";

export const PAGE_TYPES = [
  { value: "excursions_visites", label: "Excursions / Visites" },
  { value: "google_maps", label: "Google Maps" },
  { value: "inclusions", label: "Inclusions" },
  { value: "cancellation_playlist_copyrights", label: "Cancellation fees / Playlist / Copyrights" },
  { value: "useful_links", label: "Useful links" },
  { value: "options", label: "Options" },
];

/** Old type values → new ones (pages created before the rename). */
const LEGACY_TYPE_MAP = {
  rich_text: "excursions_visites",
  destinations: "excursions_visites",
  playlists: "cancellation_playlist_copyrights",
  map_itinerary: "google_maps",
};

const PAGE_TYPE_VALUES = new Set(PAGE_TYPES.map((item) => item.value));

export function normalizePageType(type) {
  const value = String(type || "").trim();
  if (PAGE_TYPE_VALUES.has(value)) return value;
  if (LEGACY_TYPE_MAP[value]) return LEGACY_TYPE_MAP[value];
  return DEFAULT_PAGE_TYPE;
}

export function pageTypeLabel(type) {
  const normalized = normalizePageType(type);
  const match = PAGE_TYPES.find((item) => item.value === normalized);
  return match ? match.label : type || "—";
}

/** Label used in admin lists / pickers (not the printed program title). */
export function pageInternalLabel(page) {
  if (!page || typeof page !== "object") return "Untitled";
  const internal = String(page.internal_title || page.internalTitle || "").trim();
  if (internal) return internal;
  const display = String(page.title || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)[0];
  return display || "Untitled";
}

/** Google Maps pages are full-bleed image sheets (no title block). */
export function isMapPageType(type) {
  return normalizePageType(type) === "google_maps";
}

export function defaultContentForType() {
  return { html: "" };
}

function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function legacyBlocksToHtml(blocks) {
  return (blocks || [])
    .map((block) => {
      if (!block) return "";
      if (block.type === "heading") return `<h2>${escapeHtml(block.text)}</h2>`;
      if (block.type === "paragraph") {
        const body = block.html || escapeHtml(block.text || "").replace(/\n/g, "<br>");
        return `<p>${body}</p>`;
      }
      if (block.type === "image" && block.image_id) {
        return `<p><em>[Image ${escapeHtml(block.image_id)} — re-insert via editor]</em></p>`;
      }
      return "";
    })
    .filter(Boolean)
    .join("");
}

function legacyItemsToHtml(type, items) {
  const normalized = normalizePageType(type);
  if (normalized === "cancellation_playlist_copyrights" || type === "playlists") {
    const lis = (items || [])
      .map((item) => {
        const title = escapeHtml(item?.title || "");
        const platform = item?.platform ? ` (${escapeHtml(item.platform)})` : "";
        const url = item?.url ? `<br><a href="${escapeHtml(item.url)}">${escapeHtml(item.url)}</a>` : "";
        return `<li><strong>${title}</strong>${platform}${url}</li>`;
      })
      .join("");
    return lis ? `<ul>${lis}</ul>` : "";
  }
  return (items || [])
    .map((item) => {
      const name = escapeHtml(item?.name || "");
      const text = escapeHtml(item?.text || "").replace(/\n/g, "<br>");
      return `<h2>${name}</h2><p>${text}</p>`;
    })
    .join("");
}

/** Normalize any stored content shape to `{ html }`, migrating legacy block/item payloads. */
export function normalizePageContent(type, content) {
  if (typeof content === "string") {
    return { html: content };
  }
  const base = content && typeof content === "object" ? content : {};
  if (typeof base.html === "string") {
    return { html: base.html };
  }
  if (Array.isArray(base.blocks)) {
    return { html: legacyBlocksToHtml(base.blocks) };
  }
  if (Array.isArray(base.items)) {
    return { html: legacyItemsToHtml(type, base.items) };
  }
  if (base.image_id) {
    return {
      html: `<p><em>[Map image ${escapeHtml(base.image_id)} — re-insert via editor]</em></p>`,
    };
  }
  return { html: "" };
}

export function clonePageContent(content) {
  return JSON.parse(JSON.stringify(normalizePageContent(null, content)));
}

/** Split rich HTML on TipTap horizontal rules (`<hr>`) into page chunks. */
export function splitHtmlByPageBreaks(html) {
  const source = String(html || "");
  if (!source.trim()) return [];
  return source
    .split(/<hr\b[^>]*>/i)
    .map((chunk) => chunk.trim())
    .filter((chunk) => {
      const text = chunk
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();
      return text.length > 0 || /<img\b/i.test(chunk);
    });
}
